/**
 * UserInfo Component
 * ------------------
 * This component displays:
 * - User avatar (clickable if provided)
 * - User name (only when sidebar is expanded OR `showDetail` is true)
 * - CognitoUI (authentication-related UI, e.g., sign out / settings)
 *
 * Data Flow:
 * 1. Gets `showSidebar` state from SidebarContext → controls expanded/collapsed state.
 * 2. Gets `name` and `profile_pic` from Redux store (userSlice).
 * 3. Uses `useMemo` to compute whether to show detailed info (`isShowed`).
 * 4. Renders Avatar → Name → CognitoUI depending on conditions.
 *
 * Workflow Diagram:
 *
 *  ┌────────────────────┐
 *  │ Redux userSlice    │───────┐
 *  │  - name            │       │
 *  │  - profile_pic     │       │
 *  └────────────────────┘       │
 *                               ▼
 *  ┌────────────────────┐   ┌──────────────┐
 *  │ SidebarContext      │   │  Props       │
 *  │  - showSidebar      │   │  - showDetail│
 *  └────────────────────┘   └──────────────┘
 *              │                     │
 *              └─────► Compute `isShowed` ◄─────┘
 *                             │
 *               ┌─────────────┴─────────────┐
 *               ▼                           ▼
 *       Show Avatar (optional)     Show Name + CognitoUI
 *       (always visible if prop)   (only if isShowed === true)
 *
 */

import React, { useCallback, useMemo } from "react";
import { Divider, Stack } from "@mui/material";
import { DynamicAvatar, OverflowTooltip } from "Reuse/UI";
import { useSidebarContext } from "../context/SidebarStateContext";
import { useSelector } from "react-redux";
import { getUserStoreData } from "store/slices";
import { useMenuSelectionContext } from "../context/MenuSelectionContext";
import { isValidValue } from "utils";
import LogoutUI from "components/LogoutUI";
import { useNavigate } from "react-router";

const UserInfo = ({ showDetail = false, showAvtar = true }) => {
  const navigate = useNavigate();

  // Sidebar context → tells if the sidebar is open/closed
  const { showSidebar } = useSidebarContext();

  // Menu context → manages selected menu index & toggle for mini sidebar
  const { toggleSelectedIndex, selectedIndex } = useMenuSelectionContext();

  const { name, profile_pic } = useSelector(getUserStoreData); // User data from Redux store

  // Show detailed info if either the sidebar is open OR showDetail is true
  const isShowed = useMemo(
    () => showSidebar || showDetail,
    [showSidebar, showDetail],
  );

  /**
   * Handle user avatar/profile click:
   * Workflow:
   *  1. If sidebar is **closed** → toggle mini sidebar profile panel
   *  2. If sidebar is **open** → navigate to "/profile_setting"
   */
  const userClick = useCallback(() => {
    if (!showSidebar && !isValidValue(selectedIndex)) {
      toggleSelectedIndex(); // open mini profile panel
    } else {
      if (isValidValue(selectedIndex)) {
        setTimeout(() => toggleSelectedIndex(), 100); // open mini profile panel
      }
    }
  }, [toggleSelectedIndex, selectedIndex, showSidebar]);

  return (
    <React.Fragment>
      <Divider variant="middle" />
      <Stack
        direction="row"
        sx={{ p: 2, gap: 1, alignItems: "center", height: 84 }}
      >
        <Stack
          direction={"row"}
          onClick={userClick}
          sx={{ alignItems: "center", gap: 1, flexGrow: 1, ...(theme) => ({ ...theme.mixins.overflow }) }}
        >
          {/* Avatar - always visible if showAvtar = true */}
          {showAvtar ? (
            <DynamicAvatar
              sizes="small"
              src={profile_pic}
              sx={{ width: 36, height: 36 }}
            />
          ) : null}

          {/* User name (only when sidebar open OR showDetail true) */}
          {isShowed && (
            <OverflowTooltip
              variant="body1"
              onClick={() => navigate(`/profile_setting`)}
              sx={{
                fontWeight: 500,
                lineHeight: "16px",
                cursor: "pointer",
                "&:hover": {
                  fontWeight: 600,
                },
              }}
            >
              {name}
            </OverflowTooltip>
          )}
        </Stack>

        {/* CognitoUI (e.g., authentication controls) */}
        {isShowed && <LogoutUI />}
      </Stack>
    </React.Fragment>
  );
};

export default React.memo(UserInfo);
